import { useState, useRef, useCallback } from "react";
import CircularProgress from "@mui/material/CircularProgress";

export default function DragAndDropFile({
	isAdmin,
	file,
	examFileUploading,
	examInDatabase,
	fetchingRequestDetails,
	handleUploadFile, // existing handler from your code
	accept = "", // e.g. ".pdf,.csv,.zip"
	multiple = false, // toggle if you want multi-upload later
}) {
	const [dragActive, setDragActive] = useState(false);
	const inputRef = useRef(null);

	// Normalize both input change and drop into the same path
	const handleFiles = useCallback(
		(files) => {
			if (!files || files.length === 0) return;

			// If your handleUploadFile expects an <input> change event,
			// synthesize one here so you don't touch existing logic:
			const syntheticEvent = { target: { files } };
			handleUploadFile(syntheticEvent);
		},
		[handleUploadFile],
	);

	const onInputChange = (e) => handleFiles(e.target.files);

	const onDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(true);
	};

	const onDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		// keep active while over
		if (!dragActive) setDragActive(true);
	};

	const onDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		// Only deactivate if actually leaving the drop zone
		if (e.currentTarget.contains(e.relatedTarget)) return;
		setDragActive(false);
	};

	const onDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		const dt = e.dataTransfer;
		if (!dt) return;

		// Prefer files (ignore directories)
		const files = dt.files && dt.files.length ? dt.files : null;
		if (files) {
			handleFiles(multiple ? files : [files[0]]);
		}
	};

	// Minimal class map to avoid repeating long strings
	const baseLabel =
		"font-semibold rounded-full px-8 h-24 flex flex-col items-center justify-center border-2 transition duration-300 ease-in-out";

	// No file present: white text, green background
	const noFile =
		"bg-primary border-primary text-white hover:bg-darkGreen hover:border-darkGreen hover:cursor-pointer";

	// File uploaded: green text, gray background
	const hasFile = "bg-gray-200 border-gray-300 text-primary hover:bg-gray-300 hover:cursor-pointer";

	// Drag styles (dashed + glow)
	const dragStyles = dragActive
		? "ring-4 ring-offset-2 ring-primaryGold border-dashed scale-[1.01]"
		: "";

	// Disable interactions during upload/fetch
	const disabled = examFileUploading || fetchingRequestDetails;
	const disabledStyles = disabled ? "opacity-60 cursor-not-allowed" : "";

	const labelClass = [
		baseLabel,
		dragStyles,
		file || examInDatabase ? hasFile : noFile,
		"disabled:bg-gray-300 disabled:pointer-events-none ",
	].join(" ");

	if (!isAdmin) return null;

	return (
		<div className="flex gap-2">
			<div
				className={`relative w-80 ${disabledStyles}`}
				onDragEnter={onDragEnter}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
				aria-label="File upload dropzone"
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						inputRef.current?.click();
					}
				}}
			>
				<label
					className={labelClass}
					htmlFor="file-upload"
					onClick={(e) => {
						// Let disabled styles block clicks
						if (disabled) {
							e.preventDefault();
							e.stopPropagation();
						}
					}}
				>
					{examInDatabase && <span className="text-primary">LOADED ✓</span>}
					{examFileUploading ? (
						<CircularProgress size={24} className="text-primaryGold" />
					) : file ? (
						<p className="text-black relative">
							{dragActive ? "Drop to replace…" : `File: ${file?.name}`}
							<button
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleUploadFile({ target: { files: [] } }); // Clear file
								}}
								title="Remove file from state"
								className="absolute -top-2 -right-3
							 h-4 w-4 bg-gray-300 rounded-full flex items-center justify-center text-xs text-gray-600 hover:bg-gray-400 hover:text-gray-800 "
							>
								x
							</button>
						</p>
					) : examInDatabase ? (
						<p>{dragActive ? "Drop new file to update…" : "UPDATE WITH NEW FILE"}</p>
					) : fetchingRequestDetails ? (
						<p>LOADING …</p>
					) : (
						<p>{dragActive ? "Drop file to upload…" : "UPLOAD FILE"}</p>
					)}

					<input
						ref={inputRef}
						id="file-upload"
						type="file"
						className="hidden"
						onChange={onInputChange}
						disabled={disabled}
						accept={accept}
						multiple={multiple}
					/>
				</label>

				{/* Optional helper text */}
				{!disabled && (
					<div
						className={`absolute bottom-2 left-1/2 -translate-x-1/2 text-xs ${
							file || examInDatabase ? "text-primary" : "text-white"
						} select-none pointer-events-none`}
					>
						Drag &amp; drop or click
					</div>
				)}
			</div>
		</div>
	);
}
